import {
  EquirectangularReflectionMapping,
  LoadingManager,
  TextureLoader,
} from "three";
import type { Texture } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { HDRLoader } from "three/examples/jsm/loaders/HDRLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { Emitter } from "../utils/Emitter";
import { disposeObject3D } from "../utils/dispose";

export type AssetType = "gltf" | "texture" | "hdri";

export interface AssetEntry {
  readonly name: string;
  readonly url: string;
  readonly type: AssetType;
}

export interface LoaderEvents extends Record<string, unknown> {
  progress: { loaded: number; total: number };
  ready: { count: number };
  error: { name: string; url: string };
}

/**
 * Charge tout, puis émet `ready`. C'est le seul endroit asynchrone du
 * démarrage : les entités se construisent ensuite, de façon synchrone.
 */
export class Loader {
  readonly events = new Emitter<LoaderEvents>();

  private readonly manager = new LoadingManager();
  private readonly gltfLoader: GLTFLoader;
  private readonly textureLoader: TextureLoader;
  private readonly hdriLoader: HDRLoader;
  private readonly models = new Map<string, GLTF>();
  private readonly textures = new Map<string, Texture>();

  constructor() {
    this.gltfLoader = new GLTFLoader(this.manager).setMeshoptDecoder(
      MeshoptDecoder,
    );
    this.textureLoader = new TextureLoader(this.manager);
    this.hdriLoader = new HDRLoader(this.manager);

    this.manager.onProgress = (_url, loaded, total) => {
      this.events.emit("progress", { loaded, total });
    };
  }

  load(manifest: readonly AssetEntry[]): void {
    this.manager.onLoad = () => {
      this.events.emit("ready", { count: manifest.length });
    };

    // LoadingManager n'appelle jamais onLoad si rien n'est mis en file :
    // sans ce cas particulier, un manifeste vide bloquerait le démarrage.
    if (manifest.length === 0) {
      queueMicrotask(() => this.events.emit("ready", { count: 0 }));
      return;
    }

    for (const entry of manifest) {
      const onError = (): void =>
        this.events.emit("error", { name: entry.name, url: entry.url });
      switch (entry.type) {
        case "gltf":
          this.gltfLoader.load(
            entry.url,
            (gltf) => this.models.set(entry.name, gltf),
            undefined,
            onError,
          );
          break;
        case "texture":
          this.textureLoader.load(
            entry.url,
            (texture) => this.textures.set(entry.name, texture),
            undefined,
            onError,
          );
          break;
        case "hdri":
          this.hdriLoader.load(
            entry.url,
            (texture) => {
              // Mapping imposé par le format équirectangulaire, pas un choix esthétique.
              texture.mapping = EquirectangularReflectionMapping;
              this.textures.set(entry.name, texture);
            },
            undefined,
            onError,
          );
          break;
      }
    }
  }

  /** Échoue bruyamment : un asset manquant est un bug de manifeste, pas un cas nominal. */
  model(name: string): GLTF {
    const gltf = this.models.get(name);
    if (!gltf) throw new Error(`Loader: modèle "${name}" absent du manifeste`);
    return gltf;
  }

  texture(name: string): Texture {
    const texture = this.textures.get(name);
    if (!texture)
      throw new Error(`Loader: texture "${name}" absente du manifeste`);
    return texture;
  }

  dispose(): void {
    for (const gltf of this.models.values()) disposeObject3D(gltf.scene);
    for (const texture of this.textures.values()) texture.dispose();
    this.models.clear();
    this.textures.clear();
    this.events.dispose();
  }
}
